import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Icon } from '@/components/Icon';
import { fetchAccount } from '@/lib/api';
import { useTier } from '@/session/SessionProvider';
import { can } from '@/lib/tier';
import './AskAware.css';

/**
 * Ask Aware — natural-language Q&A across the corpus.
 *
 * Customer-facing language is always **"Ask Aware"** and **"questions"**. Never "tokens", never
 * "credits" as a generic term. The header meter shows questions remaining this month; below a
 * threshold a top-up affordance appears.
 *
 * Answers are **Perplexity-style cited**: numbered citations under the answer, each opening the
 * source meeting. An uncited answer is not shippable here — the product's whole claim is that
 * it can show you where a fact came from.
 *
 * Metered and Pro-only in the current tier map. Locked tiers still see the surface with the
 * upsell — the resident tier is the top of the funnel, not a wall.
 */
const STARTERS = [
  'Which towns near me paused battery storage this year?',
  'What did Montclair decide about the Riverside parcel?',
  'Where have data center rezonings passed since January?',
  'Which school boards approved solar agreements?',
];

/** Below this, the top-up affordance appears next to the meter. */
const LOW_REMAINING = 30;

export function AskAware() {
  const tier = useTier();
  const unlocked = can(tier, 'askAware');
  const [question, setQuestion] = useState('');
  const [asked, setAsked] = useState<string | null>(null);

  const { data: account } = useQuery({ queryKey: ['account'], queryFn: fetchAccount });
  const meter = account?.askAware ?? null;
  const remaining = meter && meter.allowance !== null ? meter.allowance - meter.used : null;
  const paid = meter?.paidBalance ?? null;

  if (!unlocked) {
    return (
      <div className="ask ask--locked">
        <Icon name="lock" className="ask__lockIcon" />
        <h1 className="ask__title">Ask Aware</h1>
        <p className="ask__lockedBody">
          Ask a question in plain language and get an answer drawn from every meeting we hold,
          with citations to the exact passages it used.
        </p>
        <Link to="/pricing" className="ask__upgrade">
          See plans
        </Link>
      </div>
    );
  }

  return (
    <div className="ask">
      <header className="ask__head">
        <div>
          <h1 className="ask__title">Ask Aware</h1>
          <p className="ask__subtitle">
            Ask across every place we cover. Every answer cites the meetings it came from.
          </p>
        </div>

        {remaining !== null && (
          <div className="ask__meter" data-low={remaining <= LOW_REMAINING}>
            <span className="ask__meterCount">{remaining}</span>
            <span className="ask__meterLabel">questions left</span>
            {/* Purchased balance shown separately — it never expires and carries over. */}
            {paid !== null && <span className="ask__meterPaid">+{paid} purchased</span>}
            {remaining <= LOW_REMAINING && (
              <Link to="/account" className="ask__topUp">
                Top up
              </Link>
            )}
          </div>
        )}
      </header>

      <form
        className="ask__form"
        onSubmit={(e) => {
          e.preventDefault();
          if (question.trim()) setAsked(question.trim());
        }}
      >
        <input
          type="text"
          value={question}
          placeholder="Ask a question about anywhere we cover"
          onChange={(e) => setQuestion(e.target.value)}
        />
        <button type="submit" disabled={!question.trim()}>
          Ask
        </button>
      </form>

      {!asked ? (
        <div className="ask__starters">
          <p className="u-meta">Try one of these</p>
          <ul>
            {STARTERS.map((starter) => (
              <li key={starter}>
                <button
                  type="button"
                  onClick={() => {
                    setQuestion(starter);
                    setAsked(starter);
                  }}
                >
                  {starter}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <article className="ask__answer">
          <p className="ask__question">{asked}</p>
          <p className="ask__body">
            Three towns in New Jersey adopted a pause on new battery-storage applications in the
            first half of 2026. Trenton adopted a six-month moratorium unanimously<sup>1</sup>,
            and Montclair extended an existing pause while its planning board finishes siting
            standards<sup>2</sup>. Edison discussed a moratorium but took no vote<sup>3</sup>.
          </p>

          {/* Numbered citations, each opening the source meeting. */}
          <ol className="ask__citations">
            <li>
              <Link to="/meeting/$meetingId" params={{ meetingId: 'trenton-2' }}>
                Trenton Township Council · 06/28/2026 · Ordinance 2026-08
              </Link>
            </li>
            <li>
              <Link to="/meeting/$meetingId" params={{ meetingId: 'montclair-0' }}>
                Montclair Township Council · 06/30/2026 · Ordinance 2026-11
              </Link>
            </li>
            <li>
              <Link to="/meeting/$meetingId" params={{ meetingId: 'edison-1' }}>
                Edison Township Council · 06/29/2026
              </Link>
            </li>
          </ol>

          <button type="button" className="ask__again" onClick={() => setAsked(null)}>
            Ask something else
          </button>
        </article>
      )}
    </div>
  );
}
